import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import usePostApprovalTableAction from 'store/postApproval/usePostApprovalTableAction';
import MenuAction from 'components/MenuAction';
import { setGlobal } from 'store/global/reducer';
import { getTimeWithTimeZone, isPendingCheck } from 'utility/general';
import { POST_APPROVAL_LIST_FETCH, GET_SIGNED_DOCUMENT } from 'store/key';
import { KEYS, setItem } from 'storage';
import { SHyperLink } from 'style/CommonStyledComponent';
import { documentTypePaths } from 'utility/postApprovalLookUp';
import CustomLink from 'components/CustomLink';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {
    APPLICANT,
    getApplicantStatusDisplay,
    REVIEWER,
    statusKeyToDisplay,
    CCO,
    CLUSTER_ADMIN,
    IRB,
    SECRETARY,
    HO_ADMIN,
    DRAFT,
    RETURNED,
    roleDisplayLookup,
} from 'utility/lookup';

const MenuActionWithPostApprovalData = ({ row, fetchPostApproval }) => {
    const postApprovalActions = usePostApprovalTableAction({ row, fetchPostApproval });

    if (postApprovalActions.length <= 0) {
        return '-';
    }

    return <MenuAction actions={postApprovalActions} row={row} checkBackEndStatus={true} documentType={'Post'} />;
};

const usePostApprovalData = ({ setDisplayItem, onSortFunc, folder }) => {
    const userRole = useSelector((state) => state.user.role);
    const rows = useSelector((state) => state.postApproval.data);
    const history = useHistory();
    const columnToDisplay = useSelector((state) => state.column.postApproval);
    const dispatch = useDispatch();
    const pageSize = useSelector((state) => state.postApproval.pageSize);
    const page = useSelector((state) => state.postApproval.page);
    const user = useSelector((state) => state.user);

    const fetchPostApproval = (key, isDesc = true) => {
        if (onSortFunc) {
            onSortFunc(null, null, key, isDesc);
        } else {
            dispatch({
                type: POST_APPROVAL_LIST_FETCH,
                payload: {
                    isDesc,
                    page: 1,
                    pageSize,
                    sort: key,
                    folder: folder === 'all' ? '' : folder,
                },
            });
        }
    };

    const getFilteredRow = () => {
        // const filteredStatus = getUserPermissions(userRole);
        // const filteredDraft = rows.filter((item) => {
        //     return filteredStatus.includes(item.status);
        // });
        return rows;
    };

    const setPageItem = () => {
        setItem(KEYS.AMEND_PAGE, page);
        setItem(KEYS.AMEND_PAGE_SIZE, pageSize);
    };

    const rawColumn = [
        {
            field: 'appNo',
            headerName: 'CRER Application No.',
            width: 130,

            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
        },
        {
            field: 'refNo',
            headerName: 'IRB/REC Reference No.',
            width: 130,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            valueGetter: (params) => {
                const { refNo } = params.row;

                return refNo ?? '-';
            },
        },
        {
            field: 'pending',
            headerName: 'Pending',
            mandatory: true,
            disableResize: true,
            width: 70,
            fixWidth: true,
            disableInColumnSelect: true,
            disableItemSort: true,
            hiddenInRoles: [APPLICANT, CCO, CLUSTER_ADMIN, IRB, SECRETARY, HO_ADMIN], // hide this column when it is not applicant role
            renderCell: (params) => {
                const isPending = user.role === REVIEWER ? isPendingCheck(params.row, user) : false; //Include APPLICANT role will cause error

                if (!isPending) {
                    return <div>-</div>;
                }

                return (
                    <div style={{ height: 24 }}>
                        <ErrorOutlineIcon style={{ color: '#DC143C' }} />
                    </div>
                );
            },
        },
        {
            field: 'subNo',
            headerName: 'Submission No.',
            width: 170,
            mandatory: false,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
        },
        {
            field: 'p1scientificTitle',
            headerName: 'Scientific Title',
            dotNotion: true,
            mandatory: true,
            textNotWrap: true,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            goToPath: (params) => {
                setDisplayItem(params.row);
                // console.log('114026 usePostApprovalData.js',)

                dispatch(
                    setGlobal({
                        sideBarApplicationId: params.row.irbNo,
                        sideBarPostApprovalId: params.row.id,
                    }),
                );
            },
            hoverComponent: (params) => {
                return (
                    <div style={{ padding: '0 5px', maxWidth: 400, lineHeight: 1.4 }}>
                        {params.row.p1scientificTitle}
                    </div>
                );
            },
            width: 250,
        },
        {
            field: 'documentType',
            headerName: 'Document Type',
            mandatory: true,
            width: 220,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            renderCell: (params) => {
                const { documentType, id, docType } = params.row;

                if (documentTypePaths[docType]) {
                    return (
                        <SHyperLink
                            onClick={() => {
                                setPageItem();
                                history.push(`${documentTypePaths[docType] + id}`);
                            }}
                        >
                            {documentType}
                        </SHyperLink>
                    );
                } else {
                    return documentType;
                }
            },
        },
        {
            field: 'coordinator',
            headerName: 'Coordinating Investigator (CI)',
            width: 160,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
        },
        {
            field: 'institution',
            headerName: 'CI Hospital/Institution',
            width: 150,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
        },
        {
            field: 'statusId',
            headerName: 'Status',
            mandatory: true,
            width: 100,
            disableItemSort: true,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            filterValue: (params) => {
                if (userRole === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.row.statusId);
            },
            valueGetter: (params) => {
                if (userRole === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.value);
            },
        },
        {
            field: 'updateDtm',
            headerName: 'Last Modified Date',
            width: 170,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            mandatory: false,
            valueGetter: (params) => {
                let date = params.getValue('updateDtm');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD HH:mm:ss') : '';
                return date;
            },
        },
        {
            field: 'expiryDtm',
            headerName: 'Expiry Date',
            width: 160,
            disableItemSort: userRole === REVIEWER,
            sortKey: 'expiryDtm',
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            mandatory: false,
            valueGetter: (params) => {
                let date = params.getValue('expiryDtm');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '-';
                return date;
            },
        },

        // {
        //     field: 'version',
        //     headerName: 'Version No.',
        //     width: 100,
        //     onSort: (key, isDesc) => {
        //         fetchPostApproval(key, isDesc);
        //     },
        //     valueGetter: (params) => {
        //         return '1';
        //         // const { version } = params.row;
        //         // return version ?? '-';
        //     },
        // },
        {
            field: 'action',
            headerName: 'Action',
            disableInColumnSelect: true,
            mandatory: true,
            disableItemSort: true,
            disableResize: true,
            width: 100,
            renderCell: (params) => {
                return <MenuActionWithPostApprovalData row={params.row} fetchPostApproval={fetchPostApproval} />;
            },
        },
    ];

    const sideBarColumn = [
        {
            field: 'documentType',
            headerName: 'Document Type',
            mandatory: true,
            width: 160,
            onSort: (key, isDesc) => {
                fetchPostApproval(key, isDesc);
            },
            renderCell: (params) => {
                const { documentType, id, docType, isUploaded } = params.row;
                return isUploaded ? (
                    <CustomLink
                        onClick={() => {
                            dispatch({
                                type: GET_SIGNED_DOCUMENT,
                                payload: {
                                    docType: docType,
                                    reportId: id,
                                },
                            });
                        }}
                        isChangeColor
                        isStopJumping
                    >
                        {documentType}
                    </CustomLink>
                ) : (
                    documentType
                );
            },
        },
        {
            field: 'coordinator',
            headerName: 'Coordinating Investigator',
            width: 170,
            mandatory: false,
            // onSort: (key, isDesc) => {
            //     fetchPostApproval(key, isDesc);
            // },
        },
        {
            field: 'createDtm',
            headerName: 'Create Date',
            width: 160,
            // onSort: (key, isDesc) => {
            //     fetchPostApproval(key, isDesc);
            // },
            mandatory: false,
            valueGetter: (params) => {
                let date = params.getValue('createDtm');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD') : '';
                return date;
            },
            // filterValue: (params) => {
            //     return params.value ? new Date(params.value).getTime() : '-';
            // },
        },
        {
            field: 'statusId',
            headerName: 'Status',
            mandatory: true,
            width: 100,
            disableItemSort: true,
            // onSort: (key, isDesc) => {
            //     fetchPostApproval(key, isDesc);
            // },
            filterValue: (params) => {
                if (userRole === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.row.statusId);
            },
            valueGetter: (params) => {
                if (userRole === APPLICANT) {
                    return getApplicantStatusDisplay(params.value);
                }
                return statusKeyToDisplay(params.value);
            },
        },
        {
            field: 'updateDtm',
            headerName: 'Last Modified Date',
            width: 170,
            // onSort: (key, isDesc) => {
            //     fetchPostApproval(key, isDesc);
            // },
            mandatory: false,
            valueGetter: (params) => {
                let date = params.getValue('updateDtm');
                date = date ? getTimeWithTimeZone(date).format('YYYY-MM-DD HH:mm:ss') : '';
                return date;
            },
        },
    ];

    const filteredColumn = rawColumn
        .filter((item) => {
            const isExist = columnToDisplay.some((displayColumn) => {
                return displayColumn.key === item.field;
            });

            return isExist;
        })
        .filter((item) => {
            if (item?.hiddenInRoles?.includes(user.role)) {
                return false;
            }

            return true;
        });

    return {
        columns: filteredColumn,
        rawColumn: rawColumn,
        rows: getFilteredRow(),
        sideBarColumn,
    };
};

export default usePostApprovalData;
